import { ActivityType } from "@/types/activity"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatDateString, formatDurationFromStrings, formatDate, formatDateForActivityTable } from "../../../lib/utils/date"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Edit, Trash2 } from "lucide-react"

interface NapTableProps {
  activities: ActivityType[]
  onActivityClick?: (activity: ActivityType) => void
  onEditActivity?: (activity: ActivityType) => void
  onDeleteActivity?: (activity: ActivityType) => void
  timezone?: string
}

export function NapTable({ 
  activities, 
  onActivityClick = () => {}, 
  onEditActivity = () => {},
  onDeleteActivity = () => {},
  timezone
}: NapTableProps) {
  if (!activities || activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
        <div className="mb-4 bg-purple-900/30 text-purple-200 p-5 rounded-full">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
            <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
            <path d="M6 1v3" />
            <path d="M10 1v3" />
            <path d="M14 1v3" />
            <path d="M18 16V8a4 4 0 0 0-4-4H6" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-purple-200 mb-2">No Nap Records Yet</h3>
        <p className="text-muted-foreground text-sm max-w-xs">
          Record your child's daytime naps to help establish consistent rest patterns.
        </p>
      </div>
    );
  }

  const handleRowClick = (activity: ActivityType) => {
    onActivityClick(activity);
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Environment</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {activities.map((activity) => (
            <TableRow 
              key={activity.id} 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => handleRowClick(activity)}
            >
              <TableCell>
                {formatDateForActivityTable(activity.startTime)}
              </TableCell>
              <TableCell onClick={() => handleRowClick(activity)} className="cursor-pointer">
                {formatDurationFromStrings(activity.startTime, activity.endTime)}
              </TableCell>
              <TableCell onClick={() => handleRowClick(activity)} className="cursor-pointer">
                <Badge variant="outline">
                  {activity.locationName || 'Not specified'}
                </Badge>
              </TableCell>
              <TableCell onClick={() => handleRowClick(activity)} className="cursor-pointer">
                {activity.environmentName || 'Not specified'}
              </TableCell>
              <TableCell onClick={() => handleRowClick(activity)} className="cursor-pointer">
                {activity.notes && activity.notes.length > 20 ? `${activity.notes.substring(0, 20)}...` : (activity.notes || '-')}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditActivity(activity);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteActivity(activity);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
